/**
 * Families, input products and the "usually becomes" links (TASK-0259, FRESH-1): the API calls and the one
 * rule every picker on these screens follows. Owner rule 2026-10-21: candidates are products with activity in
 * the last 6 months (`activity_within_days=183`): sold for the "becomes" side, bought for the "inputs" side,
 * same family first; "Show older items" lifts the filter.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type {
  FamilyBulkResponse,
  ProductFamily,
  ProductListResponse,
  ProductSummary,
} from '@/lib/api/types';

/** Six months, the owner's rule for suggested matches. */
export const ACTIVITY_DAYS = 183;

export const familyKeys = {
  all: ['product-families'] as const,
  list: () => ['product-families', 'list'] as const,
};

export function useFamilies() {
  return useQuery({
    queryKey: familyKeys.list(),
    queryFn: () => api.get<ProductFamily[]>('/products/families'),
    staleTime: 5 * 60_000,
  });
}

/** Active families, for a select. Tolerates a response that is not a list (a stubbed client). */
export function activeFamilies(list: ProductFamily[] | undefined): ProductFamily[] {
  return Array.isArray(list) ? list.filter((f) => f.active) : [];
}

export function familyName(
  list: ProductFamily[] | undefined,
  code: string | null | undefined
) {
  if (!code) return null;
  return (Array.isArray(list) ? list : []).find((f) => f.code === code)?.name ?? code;
}

export type PickerSide = 'inputs' | 'becomes';

/**
 * The picker's query: inputs (`purchasing_only=true`) for a sold product, sold products for an input; active;
 * the 6-month activity filter unless `older` is on; the same family first (`family_first`, ordered by the API
 * across every page, review N2).
 */
export function pickerParams(args: {
  side: PickerSide;
  term: string;
  older: boolean;
  family?: string | null;
}): Record<string, string | number | boolean> {
  return {
    search: args.term,
    pageSize: 25,
    active: true,
    rank: 'exact_first',
    purchasing_only: args.side === 'inputs',
    ...(args.older ? {} : { activity_within_days: ACTIVITY_DAYS }),
    ...(args.family ? { family_first: args.family } : {}),
  };
}

export function useLinkCandidates(args: {
  side: PickerSide;
  term: string;
  older: boolean;
  family: string | null | undefined;
}) {
  const term = args.term.trim();
  const family = args.family ?? null;
  return useQuery({
    queryKey: ['products', 'link-picker', args.side, term, args.older, family] as const,
    queryFn: () =>
      api.get<ProductListResponse>('/products', {
        params: pickerParams({ side: args.side, term, older: args.older, family }),
      }),
    enabled: term.length >= 2,
    staleTime: 30_000,
    select: (res) => ({
      rows: res.data ?? [],
      total: res.total ?? 0,
    }),
  });
}

export function pickerRowLabel(row: ProductSummary): string {
  return [row.familyName, row.department].filter(Boolean).join(' · ');
}

/**
 * The family sheet's CSV: `product_id, family` with an optional header; a blank family clears. A product listed
 * twice keeps its last line (the server's rule; review m3: the check then returns one row per product, and the
 * row count the stale check compares is this one), the earlier line named as skipped.
 */
export function parseFamilySheet(text: string): {
  rows: Array<{ productId: number; family: string | null }>;
  problems: string[];
} {
  const byId = new Map<
    number,
    { productId: number; family: string | null; line: number }
  >();
  const problems: string[] = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((raw, index) => {
    const line = raw.trim();
    if (line === '') return;
    const cells = line.split(/[,\t;]/).map((c) => c.trim().replace(/^"|"$/g, ''));
    const [idText, familyText = ''] = cells;
    if (index === 0 && !/^\d+$/.test(idText ?? '')) return; // the header
    if (!/^\d+$/.test(idText ?? '')) {
      problems.push(`Line ${index + 1}: "${idText}" is not a product id`);
      return;
    }
    const family = familyText.trim().toLowerCase();
    if (family !== '' && !/^[a-z_]{2,32}$/.test(family)) {
      problems.push(`Line ${index + 1}: "${familyText}" is not a family code`);
      return;
    }
    const productId = Number(idText);
    const earlier = byId.get(productId);
    if (earlier) {
      problems.push(
        `Line ${earlier.line}: product ${productId} is listed again on line ${index + 1}; the last line wins`
      );
    }
    // A repeated product keeps its first position with its last line's family.
    byId.set(productId, {
      productId,
      family: family === '' ? null : family,
      line: index + 1,
    });
  });
  const rows = [...byId.values()].map(({ productId, family }) => ({ productId, family }));
  return { rows, problems };
}

export function familySheetDryRun(
  rows: Array<{ productId: number; family: string | null }>
) {
  return api.post<FamilyBulkResponse>('/products/families/bulk', { rows, dryRun: true });
}

export function familySheetApply(
  rows: Array<{ productId: number; family: string | null }>,
  checkToken: string
) {
  return api.post<FamilyBulkResponse>(
    '/products/families/bulk',
    { rows, checkToken },
    { timeout: 300_000 }
  );
}

export const SHEET_STATUS_LABEL: Record<
  FamilyBulkResponse['rows'][number]['status'],
  string
> = {
  new: 'Family set',
  changed: 'Family changed',
  same: 'Same as now',
  cleared: 'Family cleared',
  unknown_product: 'No such product',
  unknown_family: 'No such family',
  inactive: 'Inactive product',
  input_needs_family: 'An input keeps its family',
  changed_since_check: 'Changed after the check',
};
